/**
 * Configuration API - RTK Query Service with SessionStorage
 * 
 * This module provides RTK Query endpoints for managing application configuration:
 * - SessionStorage caching with 1-hour TTL (persists across page refreshes)
 * - Only fetches from API if config doesn't exist or is stale in sessionStorage
 * - Automatic request deduplication
 * - Fallback to cached data if API fails
 * - Optimistic updates support
 * 
 * Benefits:
 * - No unnecessary API calls on page refresh
 * - Configs persist in sessionStorage until browser tab is closed
 * - Combines RTK Query's request deduplication with sessionStorage persistence
 */

import { apiService } from './apiService';
import { ConfigType, ConfigValues, InternalConfigType } from '../utils/configManager';
import { getSessionStorage, setSessionStorage } from '../utils/sessionStorage';

/**
 * Response from the config fetch endpoint
 */
interface ConfigResponse {
    id: number;
    cdate: string;
    config_name: string;
    display_name: string;
    description: string;
    config_value: ConfigValues;
}

/**
 * Check if cached config is stale (older than 1 hour)
 */
export const isConfigStale = (configName: string): boolean => {
    try {
        const internalConfig = getSessionStorage(configName) as InternalConfigType | null;

        if (!internalConfig?.last_checked) {
            return true;
        }

        const lastChecked = new Date(internalConfig.last_checked);

        if (isNaN(lastChecked.getTime())) {
            return true;
        }

        const now = new Date().getTime();
        const lastCheckedTime = lastChecked.getTime();
        const oneHourInMs = 60 * 60 * 1000;

        return (now - lastCheckedTime) > oneHourInMs;
    } catch (_error) {
        return true;
    }
};

/**
 * Get config from sessionStorage
 */
export const getCachedConfig = (configName: string): ConfigType | null => {
    try {
        const internalConfig = getSessionStorage(configName) as InternalConfigType | null;
        return internalConfig?.config || null;
    } catch (_error) {
        return null;
    }
};

/**
 * Store config in sessionStorage
 */
export const setCachedConfig = (configName: string, config: ConfigType): void => {
    try {
        const internalConfig: InternalConfigType = {
            last_checked: new Date(),
            config: config
        };
        setSessionStorage(configName, internalConfig);
    } catch (_error) {
        // Silently fail if sessionStorage is unavailable
    }
};

/**
 * Transform API response to ConfigType
 */
export const transformConfigResponse = (response: ConfigResponse): ConfigType => ({
    id: response.id,
    cdate: new Date(response.cdate),
    config_name: response.config_name,
    display_name: response.display_name,
    description: response.description,
    config_value: response.config_value
});

/**
 * Validate config name
 */
export const isValidConfigName = (configName: string | null | undefined): boolean => {
    return !!(configName && (typeof configName === 'string') && configName.trim() !== '');
};

/**
 * Extended API service with config endpoints
 */
export const configApi = apiService.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetch configuration by name
         * 
         * Features:
         * - SessionStorage caching with 1 hour TTL
         * - Only fetches from API if not in sessionStorage or stale
         * - Request deduplication (multiple calls with same config_name return same promise)
         * - Automatic background refetch when data becomes stale
         */
        getConfig: builder.query<ConfigType, string>({
            queryFn: async (configName, _api, _extraOptions, baseQuery) => {
                // Validate config name - prevent empty/undefined/null config names from triggering API calls
                if (!isValidConfigName(configName)) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Invalid config name provided',
                            data: null
                        }
                    };
                }

                // Check sessionStorage first
                const cachedConfig = getCachedConfig(configName);

                if (cachedConfig && !isConfigStale(configName)) {
                    // Return cached config if it exists and is not stale
                    return { data: cachedConfig };
                }

                // Fetch from API if not cached or stale
                const result = await baseQuery({
                    url: '/portal/config/',
                    method: 'POST',
                    body: { config_name: configName }
                });

                if (result.error) {
                    // If fetch fails and we have cached data, return cached data
                    if (cachedConfig) {
                        return { data: cachedConfig };
                    }

                    return { error: result.error };
                }

                // Transform and cache the response
                const response = result.data as ConfigResponse;
                const transformedData = transformConfigResponse(response);

                // Store in sessionStorage
                setCachedConfig(configName, transformedData);

                return { data: transformedData };
            },
            // Cache config data in Redux for 1 hour (3600 seconds)
            keepUnusedDataFor: 3600,
            // Provide a unique cache key based on config name
            providesTags: (result, error, configName) =>
                result ? [{ type: 'Config' as const, id: configName }] : []
        }),

        /**
         * Refresh configuration by name
         * Forces a fresh fetch from the server and updates the cache
         */
        refreshConfig: builder.mutation<ConfigType, string>({
            queryFn: async (configName, _api, _extraOptions, baseQuery) => {
                // Validate config name - prevent empty/undefined/null config names from triggering API calls
                if (!isValidConfigName(configName)) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Invalid config name provided for refresh',
                            data: null
                        }
                    };
                }

                const result = await baseQuery({
                    url: '/portal/config/',
                    method: 'POST',
                    body: { config_name: configName }
                });

                if (result.error) {
                    return { error: result.error };
                }

                // Transform and return the response
                const response = result.data as ConfigResponse;
                const transformedData = transformConfigResponse(response);

                return { data: transformedData };
            },
            // Invalidate the cache for this config to trigger refetch.
            // Only on success — invalidating on error would trigger a redundant
            // refetch of an endpoint that is already unreachable.
            invalidatesTags: (result, error, configName) =>
                error ? [] : [{ type: 'Config' as const, id: configName }]
        })
    }),
    overrideExisting: false
});

// Export hooks for usage in components
export const {
    useGetConfigQuery,
    useRefreshConfigMutation,
    useLazyGetConfigQuery
} = configApi;
